import type { CSSProperties, MouseEventHandler, ReactNode } from 'react'
import { forwardRef, useEffect, useRef, useState } from 'react'

import * as TooltipRadix from '@radix-ui/react-tooltip'

import { classNames } from '@/utils/helpers.ts'

export interface TooltipProps {
  children?: ReactNode
  trigger?: ReactNode
  content: string | ReactNode
  contentClassName?: string
  className?: string
  side?: 'top' | 'right' | 'bottom' | 'left'
  align?: 'start' | 'center' | 'end'
  sideOffset?: number
  onClick?: MouseEventHandler<HTMLButtonElement>
  open?: boolean
  onOpenChange?: (state: boolean) => void
  disabled?: boolean
  style?: CSSProperties
  asChild?: boolean
  testId?: string
}

export const Tooltip = forwardRef<HTMLButtonElement, TooltipProps>(
  (
    {
      side,
      align,
      sideOffset,
      trigger,
      children,
      onClick,
      asChild,
      contentClassName,
      content,
      className,
      open,
      onOpenChange,
      disabled,
      style,
      testId,
    },
    ref,
  ) => {
    const [internalOpen, setInternalOpen] = useState(false)
    const triggerRef = useRef<HTMLButtonElement | null>(null)

    useEffect(() => {
      if (triggerRef.current) {
        const handleMouseEnter = () => {
          setInternalOpen(true)
        }
        const handleMouseLeave = () => {
          setInternalOpen(false)
        }
        const trigger = triggerRef.current

        trigger.addEventListener('mouseenter', handleMouseEnter)
        trigger.addEventListener('mouseleave', handleMouseLeave)
        trigger.addEventListener('click', handleMouseLeave)

        return () => {
          trigger?.removeEventListener('mouseenter', handleMouseEnter)
          trigger?.removeEventListener('mouseleave', handleMouseLeave)
          trigger?.removeEventListener('click', handleMouseLeave)
        }
      }
    }, [])

    return (
      <TooltipRadix.Provider delayDuration={200}>
        <TooltipRadix.Root
          open={open ?? internalOpen}
          onOpenChange={onOpenChange}
        >
          {trigger ? (
            trigger
          ) : (
            <TooltipRadix.Trigger
              type={!asChild ? 'button' : undefined}
              onClick={onClick}
              className={classNames(
                className,
                disabled && 'pointer-events-none',
              )}
              ref={(elementsRef) => {
                triggerRef.current = elementsRef
                if (ref && typeof ref === 'function') {
                  ref(elementsRef)
                }
              }}
              style={style}
              asChild={asChild}
              data-testid={testId}
            >
              {children}
            </TooltipRadix.Trigger>
          )}
          {content && (
            <TooltipRadix.Portal>
              <TooltipRadix.Content
                side={side}
                align={align}
                className={classNames(
                  'z-[10000] min-w-[120px] rounded bg-white px-2 py-1.5 text-center text-xs font-medium text-secondary-500 shadow dark:bg-secondary-600 dark:text-white',
                  contentClassName,
                )}
                sideOffset={sideOffset ?? 5}
              >
                {content}
                <TooltipRadix.Arrow className="fill-white shadow-lg dark:fill-secondary-600" />
              </TooltipRadix.Content>
            </TooltipRadix.Portal>
          )}
        </TooltipRadix.Root>
      </TooltipRadix.Provider>
    )
  },
)

Tooltip.displayName = 'Tooltip'
